import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import * as XLSX from 'xlsx';
import {
  fetchWaApTemplates,
  mapTemplateToCampaign,
  uploadCampaignMedia,
  sendCampaignMessage,
  sendCustomizedCampaignMessage,
} from '../../../lib/api';
import TokenService from '../../../lib/tokenService';
import TemplatePreview from '../components/TemplatePreview';
import '../styles/send-template.css';

const extractPlaceholderIndexes = (text = '') => {
  const matches = [...text.matchAll(/{{(\d+)}}/g)].map((match) => Number(match[1]));
  const unique = Array.from(new Set(matches));
  return unique.sort((a, b) => a - b);
};

const INDIAN_MOBILE = /^[6-9]\d{9}$/;

// Indian mobiles are 10 digits. 91 / +91 (and a leading 0 or 00) is only the country code.
const normaliseNumber = (value) => {
  if (value === null || value === undefined) return null;
  const raw = value.toString().trim();
  if (!raw) return null;

  let digits = raw.replace(/\D/g, '');
  if (!digits) return null;

  if (digits.startsWith('00')) digits = digits.slice(2);

  if (digits.length === 12 && digits.startsWith('91')) {
    digits = digits.slice(2);
  } else if (digits.length === 11 && digits.startsWith('0')) {
    digits = digits.slice(1);
  }

  if (!INDIAN_MOBILE.test(digits)) return null;
  return `+91${digits}`;
};

const MAX_MANUAL_NUMBERS = 5;

const computeNumberStats = (entries) => {
  const tokens = entries
    .map((entry) => (entry ?? '').toString().trim())
    .filter((entry) => entry.length > 0);
  const seen = new Set();
  const duplicates = [];
  const valid = [];
  const invalid = [];

  tokens.forEach((token) => {
    const normalised = normaliseNumber(token);
    if (!normalised) {
      invalid.push(token);
      return;
    }
    if (seen.has(normalised)) {
      duplicates.push(normalised);
      return;
    }
    seen.add(normalised);
    valid.push(normalised);
  });

  return {
    total: tokens.length,
    valid,
    invalid,
    duplicates,
  };
};

const SendTemplate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const campaignId = useMemo(() => new URLSearchParams(location.search).get('template_id'), [location.search]);
  const headerPreviewRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templatesError, setTemplatesError] = useState('');
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [headerConfig, setHeaderConfig] = useState({
    hasComponent: false,
    type: 'NONE',
    text: '',
    placeholders: [],
    values: [],
    mediaPreviewUrl: '',
    mediaIds: { image: '', video: '', document: '' },
    location: { latitude: '', longitude: '', name: '', address: '' },
  });
  const [bodyConfig, setBodyConfig] = useState({
    hasComponent: false,
    text: '',
    placeholders: [],
    values: [],
  });
  const [footerText, setFooterText] = useState('');
  const [buttons, setButtons] = useState([]);
  const [templateMeta, setTemplateMeta] = useState({ language: 'en_US', name: '' });
  const [mappingState, setMappingState] = useState({ status: 'idle', message: '' });
  const [numbersInput, setNumbersInput] = useState('');
  const [manualStats, setManualStats] = useState({ total: 0, valid: [], invalid: [], duplicates: [] });
  const [fileStats, setFileStats] = useState({ total: 0, valid: [], invalid: [], duplicates: [] });
  const [fileSource, setFileSource] = useState(null);
  const [sendMode, setSendMode] = useState('manual');
  const [statusBanner, setStatusBanner] = useState({ severity: 'info', message: '' });
  const [sending, setSending] = useState(false);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [hasUploaded, setHasUploaded] = useState(false);
  const [isLoadingFile, setIsLoadingFile] = useState(false);
  const [totalNumbers, setTotalNumbers] = useState(0);
  const [duplicateCount, setDuplicateCount] = useState(0);
  const [incorrectCount, setIncorrectCount] = useState(0);
  const [manualPhones, setManualPhones] = useState(['']);
  const [recipientMode, setRecipientMode] = useState('type'); // 'type' | 'file' — only when variables-from-file is off
  const [fileInputKey, setFileInputKey] = useState(0);

  const token = TokenService.getToken();
  const userId = useMemo(() => {
    if (!token) return null;
    try {
      const decoded = jwtDecode(token);
      return decoded?.id || null;
    } catch (err) {
      console.error('Failed to decode token', err);
      return null;
    }
  }, [token]);

  const loadTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    setTemplatesError('');
    try {
      const response = await fetchWaApTemplates();
      setTemplates(response?.templates || []);
    } catch (error) {
      console.error('Failed to fetch templates', error);
      setTemplatesError(error?.message || 'Failed to load templates.');
    } finally {
      setTemplatesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadTemplates();
  }, [loadTemplates]);

  const resetTemplateState = useCallback(() => {
    if (headerPreviewRef.current) {
      URL.revokeObjectURL(headerPreviewRef.current);
      headerPreviewRef.current = null;
    }
    setHeaderConfig({
      hasComponent: false,
      type: 'NONE',
      text: '',
      placeholders: [],
      values: [],
      mediaPreviewUrl: '',
      mediaIds: { image: '', video: '', document: '' },
      location: { latitude: '', longitude: '', name: '', address: '' },
    });
    setBodyConfig({ hasComponent: false, text: '', placeholders: [], values: [] });
    setFooterText('');
    setButtons([]);
    setTemplateMeta({ language: 'en_US', name: '' });
    setNumbersInput('');
    setManualStats({ total: 0, valid: [], invalid: [], duplicates: [] });
    setFileStats({ total: 0, valid: [], invalid: [], duplicates: [] });
    setFileSource(null);
    setStatusBanner({ severity: 'info', message: '' });
    setIsManualEntry(false);
    setHasUploaded(false);
    setIsLoadingFile(false);
    setTotalNumbers(0);
    setDuplicateCount(0);
    setIncorrectCount(0);
    setManualPhones(['']);
    setRecipientMode('type');
    setFileInputKey((k) => k + 1);
  }, []);

  const parseTemplate = useCallback((template) => {
    if (!template) return;
    let parsedCode;
    try {
      parsedCode = JSON.parse(template.code || '{}');
    } catch (error) {
      console.error('Failed to parse template code', error);
      parsedCode = {};
    }

    const components = Array.isArray(parsedCode?.components) ? parsedCode.components : [];
    const headerComponent = components.find((component) => component.type === 'HEADER');
    const bodyComponent = components.find((component) => component.type === 'BODY');
    const footerComponent = components.find((component) => component.type === 'FOOTER');
    const buttonsComponent = components.find((component) => component.type === 'BUTTONS');

    const headerPlaceholders = headerComponent?.text ? extractPlaceholderIndexes(headerComponent.text) : [];
    const headerExamples = headerComponent?.example?.header_text || [];
    const headerValues = headerPlaceholders.map((index) => headerExamples[index - 1] || '');

    setHeaderConfig({
      hasComponent: Boolean(headerComponent),
      type: headerComponent?.format || (headerComponent ? 'TEXT' : 'NONE'),
      text: headerComponent?.text || '',
      placeholders: headerPlaceholders,
      values: headerValues,
      mediaPreviewUrl: '',
      mediaIds: { image: '', video: '', document: '' },
      location: { latitude: '', longitude: '', name: '', address: '' },
    });

    const bodyPlaceholders = bodyComponent?.text ? extractPlaceholderIndexes(bodyComponent.text) : [];
    const bodyExamples = bodyComponent?.example?.body_text?.flat?.() || [];
    const bodyValues = bodyPlaceholders.map((index) => bodyExamples[index - 1] || '');

    setBodyConfig({
      hasComponent: Boolean(bodyComponent),
      text: bodyComponent?.text || '',
      placeholders: bodyPlaceholders,
      values: bodyValues,
    });

    setFooterText(footerComponent?.text || '');

    const parsedButtons = Array.isArray(buttonsComponent?.buttons)
      ? buttonsComponent.buttons.map((button) => ({ text: button.text, type: button.type }))
      : [];
    setButtons(parsedButtons);

    setTemplateMeta({
      language: parsedCode?.language || template.language || 'en_US',
      name: template.name || '',
    });
  }, []);

  const handleTemplateChange = async (event) => {
    const value = event.target.value;
    setSelectedTemplateId(value);
    resetTemplateState();

    const template = templates.find((item) => String(item.id) === String(value));
    if (!template) return;

    setLoading(true);
    parseTemplate(template);

    if (campaignId && userId) {
      setMappingState({ status: 'loading', message: 'Linking template to campaign…' });
      try {
        await mapTemplateToCampaign({ templateId: campaignId, waTemplateId: template.id, userId });
        setMappingState({ status: 'success', message: 'Template linked to campaign successfully.' });
        setTimeout(() => {
          setLoading(false);
          setModalMessage('Template selected successfully!');
          setShowModal(true);
          setTimeout(() => setShowModal(false), 2000);
        }, 2000);
      } catch (error) {
        console.error('Failed to map template to campaign', error);
        setMappingState({
          status: 'error',
          message: error?.response?.data?.message || error?.message || 'Failed to map template to campaign.',
        });
        setLoading(false);
      }
    } else if (campaignId && !userId) {
      setMappingState({ status: 'error', message: 'Unable to resolve current user. Please login again.' });
      setLoading(false);
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (sendMode === 'manual') {
      const tokens = numbersInput.split(/[\s,;]+/);
      const stats = computeNumberStats(tokens);
      setManualStats(stats);
    }
  }, [numbersInput, sendMode]);

  const validateNumbers = (numbers) => {
    const invalidNumbers = [];
    const seen = new Set();
    const validNumbers = [];
    const duplicates = [];

    numbers.forEach((num) => {
      const normalized = normaliseNumber(num);
      if (!normalized) {
        invalidNumbers.push(num);
      } else if (seen.has(normalized)) {
        duplicates.push(normalized);
      } else {
        seen.add(normalized);
        validNumbers.push(normalized);
      }
    });

    return { validNumbers, invalidNumbers, duplicates };
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setFileSource(file);
    setManualPhones(['']);
    setRecipientMode('file');
    setIsLoadingFile(true);
    setHasUploaded(true);
    setStatusBanner({ severity: 'info', message: '' });

    setTimeout(() => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const binaryStr = e.target.result;
          const workbook = XLSX.read(binaryStr, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const sheet = workbook.Sheets[sheetName];
          const data = XLSX.utils.sheet_to_json(sheet, { header: 1 });
          const numbers = data.map((row) => row[0]).filter((num) => !!num);
          const columnCount = data[0]?.length || 0;

          if (isManualEntry) {
            const bodyVariablesCount = bodyConfig.placeholders.length;
            if (columnCount !== bodyVariablesCount + 1) {
              alert(`Please upload a file with ${bodyVariablesCount + 1} columns (phone number + ${bodyVariablesCount} variable columns).`);
              setIsLoadingFile(false);
              setHasUploaded(false);
              return;
            }
            const { validNumbers, invalidNumbers, duplicates } = validateNumbers(numbers);
            setTotalNumbers(numbers.length);
            setDuplicateCount(duplicates.length);
            setIncorrectCount(invalidNumbers.length);
            setFileStats({
              total: numbers.length,
              valid: validNumbers,
              invalid: invalidNumbers,
              duplicates,
            });
            setIsLoadingFile(false);
          } else {
            const { validNumbers, invalidNumbers, duplicates } = validateNumbers(numbers);
            setTotalNumbers(numbers.length);
            setDuplicateCount(duplicates.length);
            setIncorrectCount(invalidNumbers.length);
            setFileStats({
              total: numbers.length,
              valid: validNumbers,
              invalid: invalidNumbers,
              duplicates,
            });

            if (invalidNumbers.length > 0 || duplicates.length > 0) {
              const confirmation = window.confirm(
                `Warning: Found ${invalidNumbers.length} invalid numbers and ${duplicates.length} duplicate numbers.\nDo you want to remove them and proceed with valid numbers?`
              );

              if (confirmation) {
                const cleanedNumbers = Array.from(new Set(validNumbers));
                setNumbersInput(cleanedNumbers.join(','));
              } else {
                setNumbersInput('');
              }
            } else {
              setNumbersInput(validNumbers.join(','));
            }
            setIsLoadingFile(false);
          }
        } catch (error) {
          console.error('Error processing file:', error);
          alert('Error processing file. Please ensure it is a valid Excel file.');
          setIsLoadingFile(false);
          setHasUploaded(false);
        }
      };
      reader.readAsBinaryString(file);
    }, 100);
  };

  const clearFileRecipient = () => {
    setFileSource(null);
    setHasUploaded(false);
    setIsLoadingFile(false);
    setTotalNumbers(0);
    setDuplicateCount(0);
    setIncorrectCount(0);
    setNumbersInput('');
    setFileStats({ total: 0, valid: [], invalid: [], duplicates: [] });
    setFileInputKey((k) => k + 1);
  };

  const handleManualPhoneChange = (index, value) => {
    if (hasUploaded || fileSource) clearFileRecipient();
    setRecipientMode('type');
    setManualPhones((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const addManualPhoneField = () => {
    if (manualPhones.length >= MAX_MANUAL_NUMBERS) return;
    clearFileRecipient();
    setRecipientMode('type');
    setManualPhones((prev) => [...prev, '']);
  };

  const removeManualPhoneField = (index) => {
    setManualPhones((prev) => {
      if (prev.length <= 1) return [''];
      return prev.filter((_, i) => i !== index);
    });
  };

  const typedPhoneStats = useMemo(
    () => computeNumberStats(manualPhones),
    [manualPhones]
  );

  const handleHeaderValueChange = (index, value) => {
    setHeaderConfig((prev) => {
      const nextValues = [...prev.values];
      nextValues[index] = value;
      return { ...prev, values: nextValues };
    });
  };

  const handleBodyValueChange = (index, value) => {
    setBodyConfig((prev) => {
      const nextValues = [...prev.values];
      nextValues[index] = value;
      return { ...prev, values: nextValues };
    });
  };

  const handleMediaUpload = async (event, mediaType) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingMedia(true);
    setStatusBanner({ severity: 'info', message: 'Uploading media…' });

    try {
     const response = await uploadCampaignMedia(file);
     const mediaId = response?.id || response?.data?.id;
     if (!mediaId) throw new Error('Upload did not return a media id');
     const previewUrl = mediaType === 'DOCUMENT' ? '' : URL.createObjectURL(file);
      if (headerPreviewRef.current && headerPreviewRef.current !== previewUrl) {
        URL.revokeObjectURL(headerPreviewRef.current);
      }
      headerPreviewRef.current = previewUrl || null;
      setHeaderConfig((prev) => {
        const nextMedia = { ...prev.mediaIds };
        nextMedia.image = mediaType === 'IMAGE' ? mediaId : prev.mediaIds.image;
        nextMedia.video = mediaType === 'VIDEO' ? mediaId : prev.mediaIds.video;
        nextMedia.document = mediaType === 'DOCUMENT' ? mediaId : prev.mediaIds.document;
        return {
          ...prev,
          mediaIds: nextMedia,
          mediaPreviewUrl: previewUrl,
        };
      });
     setStatusBanner({ severity: 'success', message: 'Media uploaded successfully.' });
   } catch (error) {
     console.error('Media upload failed', error);
     setStatusBanner({
        severity: 'error',
        message: error?.message || 'Failed to upload media. Please try again.',
      });
    } finally {
      setUploadingMedia(false);
   }
  };

  const handleLocationFieldChange = (field, value) => {
    setHeaderConfig((prev) => ({
      ...prev,
      location: { ...prev.location, [field]: value },
    }));
  };

  const buildTemplateComponents = () => {
    const components = [];
    if (headerConfig.hasComponent) {
      let headerParameters = [];
      switch (headerConfig.type) {
        case 'IMAGE':
          if (!headerConfig.mediaIds.image) throw new Error('Please upload an image for the header.');
          headerParameters = [{ type: 'image', image: { id: headerConfig.mediaIds.image } }];
          break;
        case 'VIDEO':
          if (!headerConfig.mediaIds.video) throw new Error('Please upload a video for the header.');
          headerParameters = [{ type: 'video', video: { id: headerConfig.mediaIds.video } }];
          break;
        case 'DOCUMENT':
          if (!headerConfig.mediaIds.document) throw new Error('Please upload a document for the header.');
          headerParameters = [{ type: 'document', document: { id: headerConfig.mediaIds.document } }];
          break;
        case 'LOCATION':
          if (!headerConfig.location.latitude || !headerConfig.location.longitude || !headerConfig.location.name || !headerConfig.location.address) {
            throw new Error('Please fill in all location details.');
          }
          headerParameters = [{
            type: 'location',
            location: {
              latitude: headerConfig.location.latitude,
              longitude: headerConfig.location.longitude,
              name: headerConfig.location.name,
              address: headerConfig.location.address,
            },
          }];
          break;
        case 'TEXT':
          headerParameters = headerConfig.placeholders.map((_, idx) => ({
            type: 'text',
            text: headerConfig.values[idx] || '',
          }));
          break;
        default:
          headerParameters = [];
      }
      components.push({ type: 'HEADER', parameters: headerParameters });
    }

    if (bodyConfig.hasComponent) {
      const bodyParameters = bodyConfig.placeholders.map((_, idx) => ({
        type: 'text',
        text: bodyConfig.values[idx] || '',
      }));
      components.push({ type: 'BODY', parameters: bodyParameters });
    }

    return components;
  };

  const handleSend = async (e) => {
    if (e) e.preventDefault();
    if (!selectedTemplateId) {
      alert('Please select a template first.');
      return;
    }

    try {
      const components = buildTemplateComponents();
      setSending(true);
      setModalMessage('Sending template...');
      setShowModal(true);

      if (isManualEntry) {
        if (!fileSource) {
          alert('Please upload a file before submitting.');
          setSending(false);
          setShowModal(false);
          return;
        }

        const formData = new FormData();
        formData.append('file', fileSource);
        formData.append('templateName', templateMeta.name);
        if (campaignId) formData.append('campaign_id', campaignId);
        formData.append(
          'templateData',
          JSON.stringify({
            name: templateMeta.name,
            language: { code: templateMeta.language || 'en_US' },
            components,
          })
        );

        await sendCustomizedCampaignMessage(formData);
        setModalMessage('Template sent successfully!');
      } else {
        let mobileNumbers = [];

        if (recipientMode === 'file') {
          mobileNumbers = numbersInput.split(',').map((num) => num.trim()).filter((num) => num);
          if (!mobileNumbers.length) {
            alert('Please provide at least one valid recipient number.');
            setSending(false);
            setShowModal(false);
            return;
          }
        } else {
          if (typedPhoneStats.total === 0) {
            alert('Please type at least one phone number, or upload an Excel file.');
            setSending(false);
            setShowModal(false);
            return;
          }
          if (typedPhoneStats.invalid.length > 0) {
            alert(`Invalid phone number(s): ${typedPhoneStats.invalid.join(', ')}`);
            setSending(false);
            setShowModal(false);
            return;
          }
          if (typedPhoneStats.valid.length > MAX_MANUAL_NUMBERS) {
            alert(`You can enter a maximum of ${MAX_MANUAL_NUMBERS} phone numbers.`);
            setSending(false);
            setShowModal(false);
            return;
          }
          mobileNumbers = typedPhoneStats.valid;
        }

        const payload = {
          to: mobileNumbers,
          templateName: templateMeta.name,
          templateData: {
            name: templateMeta.name,
            language: { code: templateMeta.language || 'en_US' },
            components,
          },
        };

        if (campaignId) payload.campaign_id = campaignId;

        await sendCampaignMessage(payload);
        setModalMessage('Template sent successfully!');
      }
    } catch (error) {
      console.error('Failed to send campaign', error);
      let errorMessage = 'An error occurred while sending the template. Please try again.';
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setModalMessage(errorMessage);
    } finally {
      setSending(false);
    }
  };

  const createTemplateRedirect = () => navigate('/main/create-template');
  
  const toggleManualEntry = () => {
    const next = !isManualEntry;
    setIsManualEntry(next);
    // Variables from File ON → only Excel; clear typed numbers
    if (next) {
      setManualPhones(['']);
      setRecipientMode('file');
    } else {
      setRecipientMode('type');
      clearFileRecipient();
      setFileSource(null);
    }
  };

  const canSend =
    !sending &&
    !uploadingMedia &&
    !isLoadingFile &&
    (isManualEntry
      ? hasUploaded
      : recipientMode === 'type'
        ? typedPhoneStats.valid.length > 0 && typedPhoneStats.invalid.length === 0
        : hasUploaded && numbersInput.length > 0);

  useEffect(() => () => {
    if (headerPreviewRef.current) {
      URL.revokeObjectURL(headerPreviewRef.current);
    }
  }, []);

  return (
    <>
      <div className="cwt">
        <div className="box part1">
          <h5>Create Message Template Here</h5>
          <p className="dar">Unlock the potential of personalized communication with our custom message templates. Design templates that resonate with your audience and enhance engagement in a meaningful way. If you haven't created one yet, now's the perfect time! Click the button below to start designing your unique template and elevate your messaging experience today!</p>
          <div className="btcre">
            <button className="button-create" onClick={createTemplateRedirect}>
              Create Message Template
            </button>
          </div>
        </div>
        <hr />
        <h4 style={{ padding: '10px 20px', color: '#00417d' }}>Send Template</h4>
        <div className="dropdown-preview-container">
          <h6>Select Your Template</h6>
          <div className="dropdown-container-mod">
            <select
              className="dropdown-mod"
              value={selectedTemplateId}
              onChange={handleTemplateChange}
              disabled={templatesLoading}
            >
              <option value="">Select Template</option>
              {templates.map((template) => (
                <option key={template.id} value={template.id}>
                  {template.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="modify-template">
          <div className="mainheadingleft">
            <form onSubmit={handleSend} className="modify-template-container">

          {headerConfig.hasComponent && (
            <div className="box">
              <h5>Header</h5>
              {headerConfig.type === 'TEXT' ? (
                <>
                  <textarea
                    className="sendinput"
                    value={headerConfig.text}
                    onChange={(e) => {}}
                    placeholder="No Header Text"
                    disabled
                    style={{ height: 'auto' }}
                  />
                  {headerConfig.placeholders.map((index, idx) => (
                    <input
                      key={index}
                      className="sendinput"
                      type="text"
                      value={headerConfig.values[idx] || ''}
                      onChange={(event) => handleHeaderValueChange(idx, event.target.value)}
                      placeholder={`Header Variable ${idx + 1}`}
                    />
                  ))}
                </>
              ) : null}

              {headerConfig.type === 'IMAGE' && (
                <>
                  <p>Select an Image for the Header:</p>
                  <input
                    type="file"
                    accept="image/jpeg"
                    onChange={(event) => handleMediaUpload(event, 'IMAGE')}
                    disabled={uploadingMedia}
                  />
                </>
              )}

              {headerConfig.type === 'VIDEO' && (
                <>
                  <p>Select a Video Document for the Header:</p>
                  <input
                    type="file"
                    accept="video/mp4"
                    onChange={(event) => handleMediaUpload(event, 'VIDEO')}
                    disabled={uploadingMedia}
                  />
                </>
              )}

              {headerConfig.type === 'DOCUMENT' && (
                <>
                  <p>Select a PDF Document for the Header:</p>
                  <input
                    type="file"
                    accept="application/pdf"
                    onChange={(event) => handleMediaUpload(event, 'DOCUMENT')}
                    disabled={uploadingMedia}
                  />
                </>
              )}

              {headerConfig.type === 'LOCATION' && (
                <>
                  <p>Location Details</p>
                  <input
                    className="sendinput"
                    type="text"
                    value={headerConfig.location.latitude}
                    onChange={(event) => handleLocationFieldChange('latitude', event.target.value)}
                    placeholder="Enter Latitude"
                  />
                  <input
                    className="sendinput"
                    type="text"
                    value={headerConfig.location.longitude}
                    onChange={(event) => handleLocationFieldChange('longitude', event.target.value)}
                    placeholder="Enter Longitude"
                  />
                  <input
                    className="sendinput"
                    type="text"
                    value={headerConfig.location.name}
                    onChange={(event) => handleLocationFieldChange('name', event.target.value)}
                    placeholder="Enter Location Name"
                  />
                  <input
                    className="sendinput"
                    type="text"
                    value={headerConfig.location.address}
                    onChange={(event) => handleLocationFieldChange('address', event.target.value)}
                    placeholder="Enter Address"
                  />
                </>
              )}
            </div>
          )}

          {bodyConfig.hasComponent && (
            <div className="box">
              <h5>Body</h5>
              <textarea
                className="sendinput"
                value={bodyConfig.text}
                onChange={(e) => {}}
                placeholder="Body Text"
                disabled
                style={{ height: 'auto' }}
              />

              {bodyConfig.placeholders.length > 0 && (
                <div className="toggle-container">
                  <label className="switch">
                    <input
                      type="checkbox"
                      checked={isManualEntry}
                      onChange={toggleManualEntry}
                    />
                    <span className="slider round"></span>
                  </label>
                  <span>Variables from File</span>
                </div>
              )}

              {!isManualEntry &&
                bodyConfig.placeholders.map((index, idx) => (
                  <input
                    key={index}
                    className="sendinput"
                    type="text"
                    value={bodyConfig.values[idx] || ''}
                    onChange={(event) => handleBodyValueChange(idx, event.target.value)}
                    placeholder={`Body Variable ${idx + 1}`}
                  />
                ))}
            </div>
          )}

          <div className="box">
            <h5>Recipient Mobile Number</h5>

            {/* Variables from File ON → Excel only */}
            {isManualEntry ? (
              <>
                <input
                  key={fileInputKey}
                  className="sendinput"
                  type="file"
                  accept=".xlsx, .xls"
                  onChange={handleFileUpload}
                />
                <span className="recipient-hint">
                  Upload an Excel file with phone number in the first column and{' '}
                  {bodyConfig.placeholders.length} variable column(s).
                </span>
                {isLoadingFile ? (
                  <p>Processing file...</p>
                ) : (
                  totalNumbers > 0 && (
                    <div style={{ marginTop: '10px' }}>
                      <p>Total Numbers: {totalNumbers}</p>
                      <p>Invalid Numbers: {incorrectCount}</p>
                      <p>Duplicate Numbers: {duplicateCount}</p>
                      <p>
                        Valid Numbers: {totalNumbers - incorrectCount - duplicateCount}
                      </p>
                    </div>
                  )
                )}
              </>
            ) : (
              <>
                <div className="recipient-mode-tabs" role="tablist" aria-label="Recipient entry method">
                  <button
                    type="button"
                    role="tab"
                    aria-selected={recipientMode === 'type'}
                    className={`recipient-mode-tab${recipientMode === 'type' ? ' is-active' : ''}`}
                    onClick={() => {
                      if (recipientMode === 'type') return;
                      clearFileRecipient();
                      setRecipientMode('type');
                      setManualPhones(['']);
                    }}
                  >
                    Type numbers
                  </button>
                  <button
                    type="button"
                    role="tab"
                    aria-selected={recipientMode === 'file'}
                    className={`recipient-mode-tab${recipientMode === 'file' ? ' is-active' : ''}`}
                    onClick={() => {
                      if (recipientMode === 'file') return;
                      setManualPhones(['']);
                      setRecipientMode('file');
                    }}
                  >
                    Upload Excel
                  </button>
                </div>

                {recipientMode === 'type' ? (
                  <>
                    <p className="recipient-hint">
                      Type up to {MAX_MANUAL_NUMBERS} Indian mobiles: 10 digits, or with 91 / +91.
                    </p>
                    <div className="manual-phones">
                      {manualPhones.map((phone, index) => (
                        <div className="manual-phone-row" key={`phone-${index}`}>
                          <input
                            className="sendinput"
                            type="tel"
                            inputMode="numeric"
                            value={phone}
                            onChange={(event) => handleManualPhoneChange(index, event.target.value)}
                            placeholder={`Phone number ${index + 1}`}
                            maxLength={16}
                          />
                          {manualPhones.length > 1 && (
                            <button
                              type="button"
                              className="manual-phone-remove"
                              onClick={() => removeManualPhoneField(index)}
                              aria-label={`Remove phone number ${index + 1}`}
                            >
                              ×
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                    {manualPhones.length < MAX_MANUAL_NUMBERS && (
                      <button
                        type="button"
                        className="manual-phone-add"
                        onClick={addManualPhoneField}
                      >
                        + Add number ({manualPhones.length}/{MAX_MANUAL_NUMBERS})
                      </button>
                    )}
                    {typedPhoneStats.total > 0 && (
                      <div className="manual-phone-stats">
                        <span>Valid: {typedPhoneStats.valid.length}</span>
                        {typedPhoneStats.invalid.length > 0 && (
                          <span className="manual-phone-stats--bad">
                            Invalid: {typedPhoneStats.invalid.length}
                          </span>
                        )}
                        {typedPhoneStats.duplicates.length > 0 && (
                          <span className="manual-phone-stats--bad">
                            Duplicates: {typedPhoneStats.duplicates.length}
                          </span>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <input
                      key={fileInputKey}
                      className="sendinput"
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={handleFileUpload}
                    />
                    <span className="recipient-hint">
                      Upload an Excel file with Indian mobiles in the first column (10 digits, or with 91 / +91).
                    </span>
                    {isLoadingFile ? (
                      <p>Processing file...</p>
                    ) : (
                      totalNumbers > 0 && (
                        <div style={{ marginTop: '10px' }}>
                          <p>Total Numbers: {totalNumbers}</p>
                          <p>Invalid Numbers: {incorrectCount}</p>
                          <p>Duplicate Numbers: {duplicateCount}</p>
                          <p>
                            Valid Numbers: {totalNumbers - incorrectCount - duplicateCount}
                          </p>
                        </div>
                      )
                    )}
                  </>
                )}
              </>
            )}
          </div>
          {canSend && (
            <div className="button-container">
              <button type="button" className="submit-button" onClick={handleSend} disabled={!canSend}>
                {sending ? 'Sending…' : 'Send'}
              </button>
            </div>
          )}
            </form>
          </div>
          <div className="box chat-previewmod">
            <h4 className="hh44">Live Chat Preview</h4>
            <TemplatePreview
          headerType={headerConfig.type}
          headerText={headerConfig.text}
          headerValues={headerConfig.values}
          bodyText={bodyConfig.text}
          bodyValues={bodyConfig.values}
          footerText={footerText}
              buttons={buttons}
              mediaPreviewUrl={headerConfig.mediaPreviewUrl}
            />
          </div>
        </div>
      </div>

      {loading && (
        <div className="loader-container">
          <div className="loader"><span></span></div>
        </div>
      )}

      {showModal && (
        <div className="modal-overlaymo">
          <div className="modal-contentmo">
            <h5>{modalMessage}</h5>
            {modalMessage === 'Sending template...' ? (
              <div className="spinner"></div>
            ) : (
              <button
                className="btn-primary"
                onClick={() => {
                  setShowModal(false);
                  if (modalMessage === 'Template sent successfully!') {
                    navigate('/main/summary');
                  }
                }}
                style={{ width: 'auto' }}
              >
                OK
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SendTemplate;
